
import { pick, get } from 'lodash';
import store from '@/store';
import i18n from '@/i18n/index';

import api from '@/services/utils/base';
import { DelimitersEnum } from '@/services/models/Enums';

let namePromisesCollection: {[type: string]: Promise<any>} = {};

export interface ExpandedReportQueryDrill extends Bemob.ReportQueryDrill {
  parentFilterValues?: string;
}

export interface ReportNavigationOptions {
  isAddTab?: boolean;
  isReplaceTab?: boolean;
  isInit?: boolean;
  tabData?: Bemob.TabData;
}

export default (ctx: any) => ({
  errorToast(msg: string, options: any) {
    ctx.bus.emit('toast-error', msg, options);
  },

  isUUID(data: string){
    return new RegExp(/([0-9a-f]+)-([0-9a-f]+)-([0-9a-f]+)-([0-9a-f]+)-([0-9a-f]+)$/).test(data);
  },

  //cache requests to name resolver
  cacheResponse(id:any, data: any){
    store.commit('tableEntityNames', {
      key: id,
      value: data
    });
  },

  getCachedResponse(id: any){
    return store.state.table.entityNames[id];
  },

  tabReturnLink(id: string){
    const { activeReports } = ctx.$store.state.table;
    const index = activeReports.map((e:any)=>e.id).indexOf(id);
    if (index >= 1)
    {
      let prev = activeReports[index - 1];
      if (prev) return prev.url + '?exitReport=' + id;
    }
    if (index == -1)
    {
      let prev = activeReports[activeReports.length - 1];
      if (prev) return prev.url + '?exitReport=' + id;
    }
    return ctx.getReturnLink(id);
  },

  getNamePromise(type: string, id: string) {
    return api.get(`${type}/name/${id}`);
  },


  getKey(type: string, id: string) {
    return `${type}_${id}`
  },

  updateName(type: string, id: string) {
    const needUpdate = store.state.table.activeReports.some((tab: any) => {
      if (tab.id === id) {
        return true;
      }

      if(tab.drill) {
        return tab.drill.some((item: any) => {
          return item.id === id
        });
      }

      return false;
    });

    return needUpdate 
      ? this.getNamePromise(type, id).then((response) => {
        const key = this.getKey(type, id);
        this.cacheResponse(key, response);
        
        ctx.bus.emit('entity-change-name', {
          type: type,
          id: id,
          name: response.data.payload.name,
          nameFull: response.data.payload.nameFull
        });
      })
      : Promise.resolve();
  },

  getDefaultReportNavigationOptions(): ReportNavigationOptions {
    return {
      isAddTab: false,
      isReplaceTab: false,
      tabData: null
    }
  },

  //Lazy name resolution
  resolveReportNavigation(drilldown: Bemob.ReportQueryDrill[], reportNavigationOptions: ReportNavigationOptions = {}) {
    reportNavigationOptions = Object.assign(this.getDefaultReportNavigationOptions(), reportNavigationOptions);
    const { 
      isAddTab, 
      isReplaceTab,
      isInit,
      tabData
    } = reportNavigationOptions;

    const { commit } = ctx.$store;
    commit('tableCustomGroups', null);
    const that = this;

    const getId = (id: any, mix: any) => {
      if (this.isUUID(id)) {
        return id;
      } else {
        return id + mix;
      }
    };

    const expandedDrilldown: ExpandedReportQueryDrill[] = drilldown.map((item) => {
      if(item.filterValues.includes(DelimitersEnum.drillFilterOptions)) {
        const keys = item.filterValues.split(DelimitersEnum.drillFilterOptions);
        return keys.map<ExpandedReportQueryDrill>((key) => ({
          ...item,
          parentFilterValues: keys[0],
          filterValues: key
        }));
      } else {
        return [ item ] as ExpandedReportQueryDrill[];
      }
    })
    .reduce((a, b) => a.concat(b), []);

    commit('tableDrildownFilter', drilldown);

    const resolvedNames = expandedDrilldown.map((params)=>{
      if (this.isUUID(params.filterValues)) {
        const cached = this.getCachedResponse(params.filterValues);
        const key = this.getKey(params.filterTables, params.filterValues);

        if (cached) {
          if(namePromisesCollection[key]) {
            delete namePromisesCollection[key];
          }
          
          return Promise.resolve(cached);
        } else {
          if(!namePromisesCollection[key]) {
            namePromisesCollection[key] = this.getNamePromise(params.filterTables, params.filterValues)
          }

          return namePromisesCollection[key]
            .then((data: any) => {
              this.cacheResponse(params.filterValues, data);
              return data;
            })
        }
      } else {
        return Promise.resolve({
          data: {
            payload: {
              id: getId(params.filterValues, params.filterTables),
              name: String(i18n.t(params.filterValues))
            }
          }
        });
      }
    });

    return Promise.all(resolvedNames).then((result: any[]) => {
      if(drilldown.length === 0){
        ctx.bus.emit('report-tab-updated');
      }
      ctx.drill = drilldown.map((item: any, drilldownIndex) => {
        let filterValues = item.filterValues;
        if(filterValues.includes(DelimitersEnum.drillFilterOptions)) {
          const keys = filterValues.split(DelimitersEnum.drillFilterOptions);
          filterValues = keys[0];
        }

        const index = expandedDrilldown.findIndex((item) => item.filterValues === filterValues);
        const children = expandedDrilldown.filter((item) => item.parentFilterValues === filterValues);
        
        let resolvedName = result[index].data.payload;

        const keys = Object.keys(resolvedName.trafficSource || resolvedName);
        if (keys.indexOf('custom1Name') !== -1) {
          let attrs = keys.filter((k: string) => k.startsWith('custom') && k.endsWith('Name'));
          const tableCustomGroups = pick(resolvedName.trafficSource || resolvedName, attrs);
          commit('tableCustomGroups', tableCustomGroups);
        }

        let link = ctx.reportTabLink;

        if (index == 0) {
          commit('tableAddTab', {
            id: getId(resolvedName.id, item.filterTables),
            drill: result.map((x: any) => x.data.payload),
            title: (resolvedName.nameFull || resolvedName.name),
            url: link,
            icon: 'fas fa-table',
            filterTables: get(drilldown, '[0].filterTables'),
            isAddTab: isAddTab,
            isReplaceTab: isReplaceTab,
            get returnLink() { return that.tabReturnLink(resolvedName.id) },
            tabData: tabData,
          });
          commit('tableCurrentReport', resolvedName.id);
          if(isInit){
            ctx.bus.emit('report-tab-added');
          }else{
            ctx.bus.emit('report-tab-updated');
          }
        }
        
        return {
          index: drilldownIndex,
          last: drilldownIndex == drilldown.length - 1,
          id: resolvedName.id,
          text: (resolvedName.nameFull || resolvedName.name),
          filterTables: item.filterTables,
          filterValues: resolvedName.id,
          filterOptions: item.filterOptions,
          filterOffset: item.filterOffset,
          filterLimit: item.filterLimit,
          workspaceId: resolvedName.workspaceId,
          children: children
            //.filter((item, index) => index !== 0)
            .map((child) => {
              const index = expandedDrilldown.findIndex((item) => item.filterValues === child.filterValues);
              const resolvedName = result[index].data.payload;
              
              return {
                ...child,
                text: (resolvedName.nameFull || resolvedName.name)
              };
            })
        }
      });

      commit('tableDrilldown', ctx.drill);
    })

  }

});
